import React, { useEffect, useState } from "react";

import { useLazyQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Close as CloseIcon,
  FiberManualRecord as FiberManualRecordIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { MobileTimePicker } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { addMinutes, differenceInMinutes, formatISO } from "date-fns";
import {
  Controller,
  DefaultValues,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { makeStyles, withStyles } from "tss-react/mui";
import * as yup from "yup";

import { graphql } from "@/gql";
import { AppointmentStatusKind, UpdateAppointmentInput } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { format, parseDate, parseTimeOfDay } from "@/utils/datetime";
import { apply, applyN } from "@/utils/functions";

import { isAppointmentFinished } from "../utils/isAppointmentFinished";
import useAppointmentCurrent from "../utils/useAppointmentCurrent";

const drawerMarginCollapsed = -450;
const drawerMargin = 0;

const useStyles = makeStyles()((theme) => ({
  drawerContainer: {
    position: "fixed",
    top: 0,
    right: 0,
    marginTop: "60px",
    width: "450px",
    height: "100%",
    backgroundColor: colors.white,
    zIndex: theme.zIndex.drawer,
    overflowY: "auto",
    overflowX: "hidden",
  },
  drawerOpen: {
    marginRight: drawerMargin,
    transition: theme.transitions.create("margin-right", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    marginRight: drawerMarginCollapsed,
    transition: theme.transitions.create("margin-right", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  drawerShadow: {
    // taken from Card component box-shadow style
    boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.2)",
  },
  form: {
    padding: "20px",
  },
  titleText: {
    fontSize: 24,
    fontWeight: 700,
    color: colors.dark,
    marginBottom: "20px",
  },
  closeButton: {
    color: "#26C9A7",
  },
  appointmentTypeIconContainer: {
    background: colors.white,
    padding: theme.spacing(2),
    borderRadius: 50,
    height: 40,
    width: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "10px",
  },
  iconBorder: {
    border: "1px solid #C8CED8",
  },
  appointmentIcon: {
    color: colors.gray,
  },
  appointmentText: {
    color: colors.gray,
  },
  appointmentTypeContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "15px",
    border: "1px solid #C8CED8",
    width: "160px",
    cursor: "pointer",
    marginRight: "20px",
    marginBottom: "20px",
  },
  onlineAppointment: {
    backgroundColor: colors.secondary,
    border: colors.secondary,
  },
  onlineAppointmentIcon: {
    color: colors.secondary,
  },
  inPersonAppointment: {
    backgroundColor: "#26C9A7",
    border: "#26C9A7",
  },
  inPersonAppointmentIcon: {
    color: "#26C9A7",
  },
  selectedAppointmentText: {
    color: colors.white,
  },
  appointmentTitle: {
    fontSize: "16px",
    fontWeight: 700,
    color: colors.dark,
    marginBottom: "12px",
  },
  fieldContainer: {
    marginBottom: "20px",
  },
  fieldLabel: {
    color: colors.gray,
    fontSize: "14px",
    marginBottom: "12px",
  },
  fields: {
    width: "100%",
  },
  backButton: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: 700,
    color: "#949BA8",
    marginBottom: "20px",
    "& svg": {
      fontSize: "16px",
      color: "#949BA8",
      marginRight: "10px",
    },
  },
  clientInfo: {
    marginBottom: "30px",
  },
  fullname: {
    fontSize: "20px",
    fontWeight: 700,
  },
  contact: {
    fontSize: "12px",
    fontWeight: 600,
    color: colors.gray,
  },
  avatar: {
    width: "56px",
    height: "56px",
  },
  schedType: {
    display: "flex",
    alignItems: "center",
    fontSize: "14px",
    color: colors.dark,
    padding: "5px 0px",
  },
  schedTypeIcon: {
    fontSize: "10px",
    marginRight: "5px",
  },
  upcomingIcon: {
    color: "#2D87BB",
  },
  currentIcon: {
    color: "#FFD98A",
  },
  cancelledIcon: {
    color: "#FF6262",
  },
  finishedIcon: {
    color: "#B8BEC9",
  },
}));

const validationSchema = yup
  .object()
  .shape({
    title: yup.string().label("Title").required(),
    startDate: yup.date().label("Date").required(),
    startTime: yup.date().label("Start time").required(),
    endTime: yup.date().label("End time").required(),
    details: yup.string().label("Details"),
  })
  .defined();

type FormValues = yup.InferType<typeof validationSchema>;

const StyledCancelButton = withStyles(Button, {
  root: {
    color: "#FF6262",
    border: "2px solid #FF6262",
    marginRight: 10,
    "&:hover": {
      color: "#FF6262",
      border: "2px solid #FF6262",
    },
  },
});

const FieldLabel = withStyles(Typography, {
  root: {
    color: colors.gray,
    fontSize: "14px",
    marginBottom: "12px",
    "& label": {
      color: "#D13F3F",
    },
  },
});

interface Props {
  id: string;
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  handleEdit: (input: UpdateAppointmentInput) => void;
}

const editAppointmentQuery = graphql(`
  query EditAppointment($id: ID!) {
    appointment(id: $id) {
      id
      title
      details
      status
      startDate
      startTime
      endDate
      endTime
      client {
        id
        email
        profile {
          id
          fullName
          mobileNumber
          avatarUrl(variant: THUMB)
        }
      }
    }
  }
`);

const EditAppointment: React.FC<Props> = ({
  id,
  open,
  onClose,
  handleEdit,
  onCancel,
}) => {
  const { classes, cx } = useStyles();

  const [fetch, { data }] = useLazyQuery(editAppointmentQuery, {
    variables: { id },
  });

  useEffect(() => {
    if (open) fetch();
  }, [open]);

  const appointment = data?.appointment ?? undefined;

  const isCurrent = useAppointmentCurrent(appointment);

  const status = ((): string | null | undefined => {
    if (isCurrent) {
      return "current";
    } else if (isAppointmentFinished(appointment)) {
      return AppointmentStatusKind.Finished;
    } else {
      return appointment?.status;
    }
  })()?.toUpperCase();

  const [currentStatus, setCurrentStatus] = useState<string>(
    status?.toUpperCase() ?? "UPCOMING"
  );

  useEffect(() => {
    if (status) {
      setCurrentStatus(status);
    }
  }, [status]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    setCurrentStatus(event.target.value as AppointmentStatusKind);
  };

  const initialValues: DefaultValues<FormValues> = {
    title: appointment?.title,
    startDate: apply(appointment?.startDate, parseDate),
    startTime: apply(appointment?.startTime, parseTimeOfDay),
    endTime: apply(appointment?.endTime, parseTimeOfDay),
    details: appointment?.details ?? undefined,
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful },
    control,
    watch,
    getValues,
    reset,
    setValue,
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    reset(initialValues);
  }, [open, isSubmitSuccessful, appointment]);

  const [dateDiffInMinutes, setDateDiffInMinutes] = useState(30);

  useEffect(() => {
    setDateDiffInMinutes(
      differenceInMinutes(
        getValues("endTime") as Date,
        getValues("startTime") as Date
      )
    );
  }, [watch("endTime")]);

  useEffect(() => {
    const newValue = addMinutes(
      getValues("startTime") as Date,
      dateDiffInMinutes
    );
    setValue(
      "endTime",
      addMinutes(getValues("startTime") as Date, dateDiffInMinutes)
    );
  }, [watch("startTime")]);

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    const formValues = validationSchema.cast(values);
    const input: UpdateAppointmentInput = {
      id: id,
      patch: {
        title: formValues.title,
        details: formValues.details,
        startDate: formatISO(formValues.startDate, { representation: "date" }),
        startTime: format({ timeofday: formValues.startTime }, "HH:mm:ss"),
        endTime: format({ timeofday: formValues.endTime }, "HH:mm:ss"),
        status:
          currentStatus == "CURRENT"
            ? AppointmentStatusKind.Upcoming
            : (currentStatus as AppointmentStatusKind),
      },
    };
    handleEdit(input);
  };
  const isFinished =
    status?.toUpperCase() == AppointmentStatusKind.Finished ||
    status?.toUpperCase() == AppointmentStatusKind.Cancelled;

  return (
    <div
      className={cx(classes.drawerContainer, [
        !open
          ? classes.drawerClose
          : [classes.drawerOpen, classes.drawerShadow],
      ])}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={classes.form}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Typography className={classes.titleText}>
              Edit Appointment
            </Typography>
            <IconButton
              className={classes.closeButton}
              size="small"
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            className={classes.clientInfo}
            display="flex"
            justifyContent="space-between"
          >
            <Box
              display="flex"
              alignItems="flex-start"
              justifyContent="flex-start"
            >
              <Box
                display="flex"
                alignItems="center"
                style={{ height: "100%" }}
              >
                <Avatar
                  src={appointment?.client.profile?.avatarUrl ?? undefined}
                  className={classes.avatar}
                  alt={"User"}
                />
              </Box>
              <Box
                marginLeft="12px"
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                textAlign="start"
              >
                <Typography variant="body1" className={classes.fullname}>
                  {appointment?.client.profile?.fullName}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className={classes.contact}
                >
                  {appointment?.client.email} |
                  {appointment?.client.profile?.mobileNumber ?? "N/A"}
                </Typography>
              </Box>
            </Box>
            <Box className={classes.schedType}>
              <Typography>
                <FiberManualRecordIcon
                  className={cx(classes.schedTypeIcon, [
                    currentStatus === "UPCOMING"
                      ? classes.upcomingIcon
                      : currentStatus === "CURRENT"
                      ? classes.currentIcon
                      : currentStatus === "CANCELLED"
                      ? classes.cancelledIcon
                      : classes.finishedIcon,
                  ])}
                />
              </Typography>
              <Select
                value={currentStatus}
                onChange={handleChange}
                color="primary"
                variant="standard"
                disabled={isFinished}
                disableUnderline
              >
                <MenuItem value={"UPCOMING"} style={{ display: "none" }}>
                  Upcoming
                </MenuItem>
                <MenuItem value={"CANCELLED"}>Cancelled</MenuItem>

                <MenuItem value={"CURRENT"} style={{ display: "none" }}>
                  Current
                </MenuItem>

                <MenuItem value={"FINISHED"}>Finished</MenuItem>
              </Select>
            </Box>
          </Box>

          <Box className={classes.fieldContainer}>
            <FieldLabel>
              Appointment Title<label>*</label>
            </FieldLabel>
            <TextField
              {...register("title")}
              className={classes.fields}
              type="text"
              placeholder="Enter appointment topic title"
            />
          </Box>
          <Box className={classes.fieldContainer}>
            <FieldLabel>
              Appointment Date<label>*</label>
            </FieldLabel>
            <Controller
              control={control}
              name="startDate"
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  onChange={(value) => field.onChange(value as Date)}
                  slotProps={{
                    textField: {
                      name: field.name,
                      fullWidth: true,
                      error: !!fieldState?.error,
                      helperText: fieldState?.error?.message,
                    },
                  }}
                />
              )}
            />
          </Box>
          <Box display="flex" style={{ margin: "0px -10px" }}>
            <Box
              className={classes.fieldContainer}
              style={{ padding: "0px 10px" }}
            >
              <FieldLabel>
                Start Time<label>*</label>
              </FieldLabel>
              <Controller
                control={control}
                name="startTime"
                render={({ field, fieldState }) => (
                  <MobileTimePicker
                    {...field}
                    onChange={(value) => value && field.onChange(value)}
                    slotProps={{
                      textField: {
                        name: field.name,
                        fullWidth: true,
                        error: !!fieldState?.error,
                        helperText: fieldState?.error?.message,
                      },
                    }}
                  />
                )}
              />
            </Box>
            <Box
              className={classes.fieldContainer}
              style={{ padding: "0px 10px" }}
            >
              <FieldLabel>
                End Time<label>*</label>
              </FieldLabel>
              <Controller
                control={control}
                name="endTime"
                render={({ field, fieldState }) => (
                  <MobileTimePicker
                    {...field}
                    onChange={(value) => value && field.onChange(value)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!fieldState?.error,
                        helperText: fieldState?.error?.message,
                      },
                    }}
                  />
                )}
              />
            </Box>
          </Box>
          <Box className={classes.fieldContainer}>
            <FieldLabel>Details</FieldLabel>
            <TextField
              {...register("details")}
              className={classes.fields}
              type="text"
              placeholder="Share other details here"
            />
          </Box>
          {!isFinished && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              marginBottom={20}
            >
              <StyledCancelButton variant="outlined" onClick={onCancel}>
                Cancel Appointment
              </StyledCancelButton>
              <Button color="primary" type="submit">
                Save Appointment
              </Button>
            </Box>
          )}
        </div>
      </form>
    </div>
  );
};

export default EditAppointment;
